import { useState } from 'react';
import { MapPin } from 'lucide-react';
import { useAzureMapsAutocomplete } from '~/shared/maps/useAzureMapsAutocomplete';
import type { AddressViewModel } from '@generated';
import { TextField } from '~/shared/forms/controls/base-fields/TextField';
import { SearchInput } from '../base-controls/text-controls/SearchInput';
import { FieldLabelWithHtml } from '../base-controls/FieldLabelWithHtml';

export function AddressField({
	onChange,
	value,
	readOnly = false
}: {
	onChange: (address: AddressViewModel | null) => void;
	value: AddressViewModel | null;
	readOnly?: boolean;
}) {
	const [searchTerm, setSearchTerm] = useState('');
	const { data, isLoading, isError, isEnabled } = useAzureMapsAutocomplete(searchTerm);
	const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);

	const handleAddressSelect = (address: AddressViewModel) => {
		onChange(address);
		setSearchTerm(address.formattedAddress);
		setShowAddressSuggestions(false);
	};

	const handleAptSuiteChange = (aptSuite: string) => {
		if (value) {
			onChange({ ...value, aptSuiteNumber: aptSuite });
		}
	};

	const options = isEnabled && !isLoading && !isError && data ? data : [];

	const showOptions =
		!isLoading &&
		!isError &&
		options.length > 0 &&
		searchTerm.length > 3 &&
		!(value && value.mapIntegrationId === options[0].mapIntegrationId && options.length === 1) &&
		showAddressSuggestions;

	return (
		<div
			className="grid grid-cols-1 gap-6 px-[2px] md:grid-cols-2"
			onFocus={() => setShowAddressSuggestions(true)}
			onBlur={e => {
				if (!e.currentTarget.contains(e.relatedTarget as Node)) {
					setShowAddressSuggestions(false);
				}
			}}
		>
			{!readOnly ? (
				<div className="md:col-span-2">
					<FieldLabelWithHtml className="mb-2" label="Search Address" required />
					<div className="relative">
						<SearchInput
							autoComplete="no-autofill"
							value={searchTerm}
							onChange={value => {
								setSearchTerm(value);
								setShowAddressSuggestions(true);
							}}
							placeholder="Search for an address..."
							onClear={
								searchTerm
									? () => {
											setSearchTerm('');
											onChange(null);
									  }
									: undefined
							}
						/>
						{showOptions && options.length > 0 && (
							<div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
								{options.map(suggestion => (
									<button
										key={suggestion.mapIntegrationId}
										type="button"
										onMouseDown={e => e.preventDefault()}
										onClick={() => handleAddressSelect(suggestion)}
										className="flex w-full items-start gap-3 border-b border-gray-100 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-gray-50"
									>
										<MapPin className="mt-0.5 size-5 shrink-0 text-gray-400" />
										<div>
											<div className="text-sm font-medium text-gray-900">{suggestion.formattedAddress}</div>
										</div>
									</button>
								))}
							</div>
						)}
					</div>
					<p className="mt-1.5 text-xs text-gray-500">
						Start typing to search for an address. Selected address will auto-fill the fields below.
					</p>
				</div>
			) : null}

			<TextField label="Street Address" value={value?.addressOne || ''} isReadOnly required className="md:col-span-2" />
			<TextField
				label="Apt/Suite #"
				value={value?.aptSuiteNumber || ''}
				onChange={handleAptSuiteChange}
				required={false}
				isReadOnly={readOnly}
				className="md:col-span-2"
			/>
			<TextField label="City" value={value?.city || ''} isReadOnly required />
			<TextField label="State" value={value?.state || ''} isReadOnly required />
			<TextField label="Zip Code" value={value?.zipCode || ''} isReadOnly required />
			<TextField label="Zip Code Ext" value={value?.zipCodeExtension || ''} isReadOnly required={false} />
		</div>
	);
}
